/**
 * Prolonged field care level derived from what Tab 3 actually holds (R1-07, 2026-10-25, Dr. Tatar).
 *
 * Before: one planner impression (Tab 1 'Medical capability', hidden, default GOOD) set the level for the whole force, with no link to the
 * medical force of Tab 3. Now the level follows the Role 1 facilities and the blood setting that Tab 3 passes to the calculation
 * (medical_configuration.facilities and blood_config). The Tab 1 value is used only when Tab 3 passes no Role 1 facility (failsafe, reported
 * as source TAB1_FALLBACK in the result).
 *
 * LEVELS (the four bands of prolongedFieldCare.js PFC_CAPABILITY; their descriptions follow the JTS Prolonged Casualty Care Guidelines):
 *   NONE         no medic or no intravenous fluid at the Role 1 facility: the medic can only hold the casualty.
 *   BASIC        a medic and intravenous fluid: extended TCCC with monitoring and fluids (the default Role 1 of the force builder).
 *   INTERMEDIATE BASIC + a provider (medical officer or physician assistant) + blood products forward (walking blood bank switched on in
 *                Tab 3, or blood units held at the Role 1 facility).
 *   ADVANCED     INTERMEDIATE + two medical officers + two nurses (monitoring) + blood units held at the Role 1 facility.
 * STATUS: EDUCATED GUESS. The thresholds are a reading of the band descriptions, not a published scale; the effect of each band on the wait
 * is PFC_WAIT_OFFSET (evacuationWindowModel.js, also an educated guess).
 * The force level is the lower median of the levels of its Role 1 facilities (half of the aid stations or more have at least that level).
 * 2026-10-25: when the Tab 3 card 'Care at the point of injury' (pointOfInjuryCare.js) holds the training of the Role 1 medics, the level is the LOWER of
 * the facility level and that training (reported as limited_by TRAINING or FACILITIES).
 */
import { pfcTrainingOf } from './pointOfInjuryCare';

// false: the Tab 1 value decides, as before. true: Tab 3 decides (numbers move: see docs/TODO_CURRENT.md 2026-10-25 R1-07).
export const PFC_FROM_TAB3_ON = true;   // switched on 2026-10-25 (Dr. Tatar); REVIEW AFTER THE TAB 3 DECISIONS ABOUT PFC (docs/TODO_CURRENT.md, TAB 3 DECISIONS)

export const PFC_FROM_FACILITIES_STATUS = Object.freeze({
  status: 'EDUCATED_GUESS',
  basis: 'Thresholds read from the PFC_CAPABILITY band descriptions (JTS Prolonged Casualty Care Guidelines); no published scale'
});

export const PFC_LEVEL_ORDER = Object.freeze(['NONE', 'BASIC', 'INTERMEDIATE', 'ADVANCED']);

// R1-08 (2026-10-25, Dr. Tatar: wire the loss of medical personnel). The medics of a brigade die at the rate of the unit they support times 1.15
// (battleCasualtyModule.calculateMedicalPersonnelCasualties). The share of them still alive takes the same share off the prolonged field care
// benefit (the part of the wait that the Role 1 medic carries, evacuationWindowModel PFC_WAIT_OFFSET): a brigade that has lost 20 percent of its
// medics keeps 80 percent of the benefit. EDUCATED GUESS (proportional; no study found). The old constants of
// MEDICAL_PERSONNEL_VULNERABILITY.combat_medic_role1 survival_without_medic 0.60 and evacuation_delay_increase 2.5 (Ukraine pattern, no source)
// stay in place unread (docs/DEAD_CODE_REGISTER.md). true: the benefit shrinks with the losses; false: the benefit is independent of them.
export const MEDIC_LOSS_REDUCES_PFC_ON = true;

/** Share of the medical personnel still alive, 0 to 1 (1 when the initial count is unknown or zero: nothing to lose). */
export function medicShareOf(remaining, initial) {
  const r = Number(remaining); const i = Number(initial);
  if (!(i > 0) || !Number.isFinite(r)) return 1;
  return Math.max(0, Math.min(1, r / i));
}

const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** The level of one Role 1 facility (a facility JSON of the force builder, as Tab 3 passes it). */
export function pfcLevelOfFacility(facility, wbbEnabled) {
  const p = facility?.personnel || {};
  const s = facility?.supplies || {};
  const medics = n(p.medics);
  const officers = n(p.medical_officers);
  const assistants = n(facility?.organic_assets?.personnel?.physician_assistant);
  const nurses = n(p.nurses);
  const bloodHere = n(s.blood_units) > 0;
  if (medics < 1 || n(s.iv_fluids) <= 0) return 'NONE';
  const provider = officers + assistants >= 1;
  const bloodForward = !!wbbEnabled || bloodHere;
  if (!(provider && bloodForward)) return 'BASIC';
  if (officers >= 2 && nurses >= 2 && bloodHere) return 'ADVANCED';
  return 'INTERMEDIATE';
}

const isRole1 = (f) => f && (f.facility_type === 'ROLE1' || f.role_level === 1);

/**
 * The force level from the medical configuration of Tab 3.
 * @param {Object} medicalConfig medical_configuration (facilities, blood_config)
 * @param {string} tab1Level PFC key of the Tab 1 medical capability (the failsafe)
 * @param {boolean} on the switch (default PFC_FROM_TAB3_ON; a parameter so that the rule can be tested in both states)
 * @returns {{level: string, source: string, role1_count: number, counts: Object}}
 */
export function derivePfcLevel(medicalConfig, tab1Level, on = PFC_FROM_TAB3_ON) {
  if (!on) return { level: tab1Level, source: 'TAB1_SETTING', role1_count: 0, counts: {} };
  const role1 = (Array.isArray(medicalConfig?.facilities) ? medicalConfig.facilities : []).filter(isRole1);
  if (role1.length === 0) return { level: tab1Level, source: 'TAB1_FALLBACK', role1_count: 0, counts: {} };
  const wbb = !!(medicalConfig?.blood_config?.wbb_enabled ?? medicalConfig?.blood_config?.wbbEnabled);
  const counts = { NONE: 0, BASIC: 0, INTERMEDIATE: 0, ADVANCED: 0 };
  const levels = role1.map((f) => pfcLevelOfFacility(f, wbb));
  levels.forEach((l) => { counts[l] += 1; });
  const sorted = levels.map((l) => PFC_LEVEL_ORDER.indexOf(l)).sort((a, b) => a - b);
  const facilityIdx = sorted[Math.floor((sorted.length - 1) / 2)];
  const training = pfcTrainingOf(medicalConfig);
  const trainingIdx = training === null ? Infinity : PFC_LEVEL_ORDER.indexOf(training);
  const level = PFC_LEVEL_ORDER[Math.min(facilityIdx, trainingIdx)];
  return { level, source: 'TAB3_FACILITIES', role1_count: role1.length, counts, facility_level: PFC_LEVEL_ORDER[facilityIdx], training, limited_by: training === null ? 'FACILITIES' : (trainingIdx < facilityIdx ? 'TRAINING' : 'FACILITIES') };
}

const cache = new WeakMap();
/** derivePfcLevel kept per medical configuration object (the calculation asks for it on every day of every brigade). */
export function derivePfcLevelCached(medicalConfig, tab1Level) {
  if (!medicalConfig || typeof medicalConfig !== 'object') return derivePfcLevel(medicalConfig, tab1Level);
  const hit = cache.get(medicalConfig);
  if (hit && hit.tab1 === tab1Level && hit.on === PFC_FROM_TAB3_ON && hit.fac === medicalConfig.facilities && hit.blood === medicalConfig.blood_config && hit.poi === medicalConfig.point_of_injury_care) return hit.value;
  const value = derivePfcLevel(medicalConfig, tab1Level);
  cache.set(medicalConfig, { tab1: tab1Level, on: PFC_FROM_TAB3_ON, fac: medicalConfig.facilities, blood: medicalConfig.blood_config, poi: medicalConfig.point_of_injury_care, value });
  return value;
}
